import React, { useState } from 'react';
import { Check, ShoppingCart, Package, MapPin, MessageCircle, CreditCard, Loader2, ShieldCheck, Lock } from 'lucide-react';
import { products, pricingOptions, shippingInfo, contactInfo } from '../mock';
import axios from 'axios';
import { trackBeginCheckout, trackWhatsAppClick } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Offer = ({ colorHeading = 'Elige el color que va contigo', headingTitle = 'Una para ti,', headingTitleAccent = 'otra para compartir', headingSubtitle = 'Lleva dos pares con precio especial y comparte la pasión con alguien más de tu familia.' }) => {
  const [selectedQuantity, setSelectedQuantity] = useState(2);
  const [selectedColors, setSelectedColors] = useState([]);
  const [shipping, setShipping] = useState('delivery');
  const [loading, setLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null); // 'payphone' or 'whatsapp'

  const selectedPricing = pricingOptions.find(opt => opt.quantity === selectedQuantity);
  
  const shippingCost = shipping === 'pickup' 
    ? 0 
    : (selectedPricing?.price >= shippingInfo.freeShippingThreshold ? 0 : shippingInfo.nationalCost);
  
  const totalWithShipping = selectedPricing?.price + shippingCost;
  const hasFreeShipping = selectedPricing?.price >= shippingInfo.freeShippingThreshold && shipping === 'delivery';

  const handleColorSelect = (colorId) => {
    if (selectedColors.includes(colorId)) {
      setSelectedColors(selectedColors.filter(id => id !== colorId));
    } else {
      if (selectedColors.length < selectedQuantity) {
        setSelectedColors([...selectedColors, colorId]);
      }
    }
  };

  const validateSelection = () => {
    if (selectedColors.length !== selectedQuantity) {
      alert(`Por favor selecciona ${selectedQuantity} ${selectedQuantity === 1 ? 'color' : 'colores'}`);
      return false;
    }
    return true;
  };

  const handlePaymentMethodClick = (method) => {
    if (!validateSelection()) return;
    setPaymentMethod(method);
    setShowCustomerForm(true);
  };

  const handleWhatsAppCheckout = () => {
    if (!validateSelection()) return;

    const selectedProductNames = selectedColors.map(id =>
      products.find(p => p.id === id)?.color
    ).join(', ');

    const source = sessionStorage.getItem('campaign_source') || 'direct';

    trackBeginCheckout({
      value: totalWithShipping,
      quantity: selectedQuantity,
      source,
      paymentMethod: 'whatsapp',
      colors: selectedColors.map(id => products.find(p => p.id === id)?.color),
    });
    trackWhatsAppClick({
      location: 'offer-checkout',
      source,
      value: totalWithShipping,
      quantity: selectedQuantity,
    });

    const message = `Hola! Quiero comprar ${selectedQuantity} ${selectedQuantity === 1 ? 'par' : 'pares'} de Gafas La Tri:\n` +
      `Colores: ${selectedProductNames}\n` +
      `Envío: ${shipping === 'delivery' ? (hasFreeShipping ? 'A domicilio (GRATIS)' : 'A domicilio (+$' + shippingInfo.nationalCost + ')') : 'Retiro en Quito'}\n` +
      `Total: $${totalWithShipping.toFixed(2)}`;

    window.open(`${contactInfo.whatsappLink}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handlePayphoneCheckout = async () => {
    if (!validateSelection()) return;

    // Validate customer info
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      alert('Por favor completa todos tus datos');
      return;
    }

    setLoading(true);

    try {
      const selectedProductNames = selectedColors.map(id =>
        products.find(p => p.id === id)?.color
      );

      const source = sessionStorage.getItem('campaign_source') || 'direct';

      trackBeginCheckout({
        value: totalWithShipping,
        quantity: selectedQuantity,
        source,
        paymentMethod: 'payphone',
        colors: selectedProductNames,
      });

      const orderData = {
        quantity: selectedQuantity,
        colors: selectedProductNames,
        shipping: shipping,
        source,
        customer: {
          name: customerInfo.name,
          email: customerInfo.email,
          phone: customerInfo.phone
        }
      };

      const response = await axios.post(`${API}/orders/create`, orderData);
      
      if (response.data.payment_url) {
        // Redirect to Payphone payment page
        window.location.href = response.data.payment_url;
      } else {
        alert('Error al generar el link de pago. Por favor intenta nuevamente.');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Error al procesar el pago. Por favor intenta nuevamente o contacta por WhatsApp.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="comprar" className="py-24 bg-gradient-to-br from-yellow-50 to-white">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-red-500 text-white px-5 py-2.5 rounded-full text-sm font-black mb-6 shadow-lg shadow-red-500/30">
              🔥 PRECIO LANZAMIENTO
            </div>
            <h2 className="text-4xl lg:text-6xl font-black text-gray-900 mb-4">
              {headingTitle}
              <span className="block mt-2 text-blue-900">{headingTitleAccent}</span>
            </h2>
            <p className="text-xl text-gray-600">{headingSubtitle}</p>
          </div>

          {/* Pricing Options */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {pricingOptions.map((option) => (
              <div
                key={option.id}
                data-testid={`pack-option-${option.quantity}`}
                onClick={() => {
                  setSelectedQuantity(option.quantity);
                  setSelectedColors([]);
                }}
                className={`relative cursor-pointer rounded-3xl p-8 transition-all duration-300 ${
                  selectedQuantity === option.quantity
                    ? 'bg-blue-900 text-white shadow-2xl scale-105 ring-4 ring-blue-900'
                    : 'bg-white border-2 border-gray-200 hover:border-blue-900 hover:shadow-xl'
                }`}
              >
                {option.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-xs font-black">
                      MÁS POPULAR
                    </span>
                  </div>
                )}

                <div className="text-center">
                  {/* Launch badge inside card */}
                  <div className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black mb-3 ${
                    selectedQuantity === option.quantity
                      ? 'bg-red-500 text-white'
                      : 'bg-red-50 text-red-600 border border-red-200'
                  }`}>
                    🔥 PRECIO LANZAMIENTO
                  </div>
                  {/* Old price strikethrough */}
                  <div className={`text-lg font-semibold line-through mb-1 ${
                    selectedQuantity === option.quantity ? 'text-blue-200' : 'text-gray-400'
                  }`}>
                    ${option.oldPrice.toFixed(2)}
                  </div>
                  {/* New launch price */}
                  <div className="text-5xl font-black mb-2">${option.price.toFixed(2)}</div>
                  <div className="text-lg font-bold mb-1">{option.label}</div>
                  <div className={`text-sm mb-4 ${
                    selectedQuantity === option.quantity ? 'text-blue-100' : 'text-gray-600'
                  }`}>
                    {option.description}
                  </div>
                  {option.savings > 0 && (
                    <div className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${
                      selectedQuantity === option.quantity
                        ? 'bg-yellow-400 text-gray-900'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      Ahorras ${option.savings.toFixed(2)}
                    </div>
                  )}
                  {option.pricePerUnit && (
                    <div className={`mt-3 text-sm font-semibold ${
                      selectedQuantity === option.quantity ? 'text-blue-100' : 'text-gray-600'
                    }`}>
                      ${option.pricePerUnit.toFixed(2)} por par
                    </div>
                  )}
                </div>

                {selectedQuantity === option.quantity && (
                  <div className="absolute top-4 right-4 bg-yellow-400 text-blue-900 rounded-full p-2">
                    <Check size={20} strokeWidth={3} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Color Selection (simplified — swatches only) */}
          <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {colorHeading}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Selecciona {selectedQuantity} {selectedQuantity === 1 ? 'color' : 'colores'}
              <span className="ml-2 font-semibold text-blue-900">
                ({selectedColors.length}/{selectedQuantity} seleccionados)
              </span>
            </p>

            <div className="grid grid-cols-3 gap-4">
              {products.map((product) => {
                const isSelected = selectedColors.includes(product.id);
                return (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => handleColorSelect(product.id)}
                    data-testid={`color-swatch-${product.color.toLowerCase()}`}
                    className={`relative flex flex-col items-center justify-center gap-3 rounded-2xl py-6 px-4 transition-all duration-300 border-2 ${
                      isSelected
                        ? 'border-blue-900 bg-blue-50 shadow-lg scale-[1.02]'
                        : 'border-gray-200 hover:border-blue-900 hover:bg-gray-50'
                    }`}
                  >
                    <div
                      className="w-14 h-14 rounded-full shadow-inner border-4 border-white ring-2 ring-gray-200"
                      style={{ backgroundColor: product.colorHex }}
                    ></div>
                    <span className="font-bold text-gray-900">{product.color}</span>
                    {isSelected && (
                      <div className="absolute top-2 right-2 bg-blue-900 text-white rounded-full p-1">
                        <Check size={14} strokeWidth={3} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Shipping Options */}
          <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Package size={28} />
              Opciones de entrega
            </h3>
            
            {hasFreeShipping && shipping === 'delivery' && (
              <div className="bg-green-50 border-2 border-green-500 rounded-2xl p-4 mb-6 flex items-center gap-3">
                <div className="bg-green-500 text-white rounded-full p-2">
                  <Check size={24} strokeWidth={3} />
                </div>
                <div>
                  <div className="font-bold text-green-900 text-lg">¡Felicidades! Envío GRATIS</div>
                  <div className="text-green-700 text-sm">Tu pedido supera los $50</div>
                </div>
              </div>
            )}
            
            {!hasFreeShipping && shipping === 'delivery' && selectedPricing?.price < shippingInfo.freeShippingThreshold && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 mb-6 flex items-center gap-3">
                <div className="bg-blue-500 text-white rounded-full p-2">
                  <Package size={20} />
                </div>
                <div>
                  <div className="font-bold text-blue-900">
                    Añade ${(shippingInfo.freeShippingThreshold - selectedPricing?.price).toFixed(2)} más para envío gratis
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid md:grid-cols-2 gap-6">
              <div
                onClick={() => setShipping('delivery')}
                className={`cursor-pointer rounded-2xl p-6 transition-all duration-300 ${
                  shipping === 'delivery'
                    ? 'bg-blue-900 text-white shadow-xl ring-4 ring-blue-900'
                    : 'border-2 border-gray-200 hover:border-blue-900'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Package size={24} />
                    <div>
                      <div className="font-bold text-lg">Envío a domicilio</div>
                      <div className={`text-sm ${
                        shipping === 'delivery' ? 'text-blue-100' : 'text-gray-600'
                      }`}>
                        {shippingInfo.coverage}
                      </div>
                    </div>
                  </div>
                  {shipping === 'delivery' && (
                    <div className="bg-yellow-400 text-blue-900 rounded-full p-1">
                      <Check size={20} strokeWidth={3} />
                    </div>
                  )}
                </div>
                <div className="text-2xl font-black">
                  {hasFreeShipping ? (
                    <span className="text-green-400">GRATIS</span>
                  ) : (
                    `$${shippingInfo.nationalCost}`
                  )}
                </div>
                {hasFreeShipping && (
                  <div className={`text-sm mt-2 ${shipping === 'delivery' ? 'text-green-300' : 'text-green-600'}`}>
                    Por compra mayor a $50
                  </div>
                )}
              </div>

              <div
                onClick={() => setShipping('pickup')}
                className={`cursor-pointer rounded-2xl p-6 transition-all duration-300 ${
                  shipping === 'pickup'
                    ? 'bg-blue-900 text-white shadow-xl ring-4 ring-blue-900'
                    : 'border-2 border-gray-200 hover:border-blue-900'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <MapPin size={24} />
                    <div>
                      <div className="font-bold text-lg">Retiro en punto</div>
                      <div className={`text-sm ${
                        shipping === 'pickup' ? 'text-blue-100' : 'text-gray-600'
                      }`}>
                        {shippingInfo.pickupLocation}
                      </div>
                    </div>
                  </div>
                  {shipping === 'pickup' && (
                    <div className="bg-yellow-400 text-blue-900 rounded-full p-1">
                      <Check size={20} strokeWidth={3} />
                    </div>
                  )}
                </div>
                <div className="text-2xl font-black text-green-600">GRATIS</div>
              </div>
            </div>
          </div>

          {/* Customer Form (shown when payment method selected) */}
          {showCustomerForm && paymentMethod === 'payphone' && (
            <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Tus datos</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nombre completo</label>
                  <input
                    type="text"
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-blue-900 focus:outline-none"
                    placeholder="Juan Pérez"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-blue-900 focus:outline-none"
                    placeholder="juan@example.com"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Teléfono</label>
                  <input
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-blue-900 focus:outline-none"
                    placeholder="+593 99 123 4567"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Order Summary & Payment Methods */}
          <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-3xl p-8 lg:p-12 text-white shadow-2xl">
            <h3 className="text-2xl font-bold mb-6">Resumen del pedido</h3>
            <div className="space-y-4 mb-8">
              <div className="flex justify-between text-lg items-center">
                <span>{selectedQuantity} {selectedQuantity === 1 ? 'par' : 'pares'} de gafas</span>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm line-through text-blue-200">${selectedPricing?.oldPrice?.toFixed(2)}</span>
                  <span className="font-bold">${selectedPricing?.price?.toFixed(2)}</span>
                </div>
              </div>
              <div className="flex justify-between text-lg">
                <span>Envío</span>
                <span className="font-bold">
                  {shipping === 'pickup' ? (
                    'GRATIS'
                  ) : hasFreeShipping ? (
                    <span className="text-green-400">GRATIS</span>
                  ) : (
                    `$${shippingInfo.nationalCost}`
                  )}
                </span>
              </div>
              {hasFreeShipping && shipping === 'delivery' && (
                <div className="bg-green-500/20 border border-green-400 rounded-lg p-3 text-sm">
                  <div className="flex items-center gap-2 text-green-300">
                    <Check size={16} />
                    <span className="font-semibold">¡Envío gratis aplicado!</span>
                  </div>
                </div>
              )}
              {selectedPricing?.savings > 0 && (
                <div className="flex justify-between text-lg text-yellow-400">
                  <span>Ahorro lanzamiento</span>
                  <span className="font-bold">-${selectedPricing.savings.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-white/30 pt-4 flex justify-between text-2xl font-black">
                <span>Total</span>
                <span className="text-yellow-400">${totalWithShipping?.toFixed(2)}</span>
              </div>
            </div>

            {/* Security Trust Badges */}
            <div className="bg-white/10 border border-white/20 rounded-xl p-4 mb-6" data-testid="security-trust-strip">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-green-500 rounded-lg p-2">
                  <ShieldCheck size={20} className="text-white" />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-sm">Compra 100% segura</div>
                  <div className="text-xs text-blue-100">Conexión cifrada SSL · No almacenamos tu tarjeta</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[11px] text-blue-100">
                <div className="flex items-center gap-1.5">
                  <Lock size={12} className="text-yellow-400 flex-shrink-0" />
                  <span>Pago cifrado SSL 256-bit</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <ShieldCheck size={12} className="text-yellow-400 flex-shrink-0" />
                  <span>Procesado por Payphone</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Check size={12} className="text-yellow-400 flex-shrink-0" />
                  <span>Sin riesgo de cargos</span>
                </div>
              </div>
            </div>

            {/* Payment Method Buttons */}
            <div className="space-y-4">
              <h4 className="text-lg font-bold mb-4">Elige tu método de pago</h4>
              
              {/* Payphone Button */}
              <button
                onClick={() => {
                  if (showCustomerForm && paymentMethod === 'payphone') {
                    handlePayphoneCheckout();
                  } else {
                    handlePaymentMethodClick('payphone');
                  }
                }}
                disabled={selectedColors.length !== selectedQuantity || loading}
                className={`w-full py-5 rounded-xl font-bold text-xl flex items-center justify-center gap-3 transition-all ${
                  selectedColors.length === selectedQuantity && !loading
                    ? 'bg-yellow-400 text-gray-900 hover:bg-yellow-500 hover:scale-105 shadow-xl cursor-pointer'
                    : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 size={24} className="animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <CreditCard size={24} />
                    {showCustomerForm && paymentMethod === 'payphone' ? 'Pagar con Payphone' : 'Pagar con Tarjeta (Payphone)'}
                  </>
                )}
              </button>
              
              {/* WhatsApp Button */}
              <button
                onClick={handleWhatsAppCheckout}
                disabled={selectedColors.length !== selectedQuantity}
                className={`w-full py-5 rounded-xl font-bold text-xl flex items-center justify-center gap-3 transition-all ${
                  selectedColors.length === selectedQuantity
                    ? 'bg-green-500 text-white hover:bg-green-600 hover:scale-105 shadow-xl cursor-pointer'
                    : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                }`}
              >
                <MessageCircle size={24} />
                Pedir por WhatsApp
              </button>
            </div>
            
            <div className="text-center mt-5 space-y-1">
              <p className="flex items-center justify-center gap-2 text-sm text-blue-50 font-semibold">
                <Lock size={14} className="text-yellow-400" />
                Tus datos viajan cifrados. Nunca vemos ni guardamos tu tarjeta.
              </p>
              <p className="text-xs text-blue-200">
                Pago procesado por <strong>Payphone</strong>, pasarela autorizada por la Superintendencia de Bancos del Ecuador.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Offer;
